/** Build-time content only. Runtime identity and API configuration have separate owners. */
export type BrandContent = Readonly<{
  id: "home";
  version: number;
  theme: "dark" | "light";
  siteName: string;
  headline: string;
  body: string;
  blocks?: readonly { id: "header-logo"; type: "image"; assetId: string; alt: string }[];
}>;

export function parseBrandContent(input: unknown): BrandContent {
  const invalid = () => {
    throw new Error("COMMAND_BRAND_CONTENT_INVALID");
  };
  if (!input || typeof input !== "object" || Array.isArray(input)) return invalid();
  const value = { ...input } as Record<string, unknown>;
  const fields = ["id", "version", "theme", "siteName", "headline", "body"];
  if (
    Object.keys(value).some((key) => ![...fields, "blocks"].includes(key)) ||
    fields.some((key) => !Object.hasOwn(value, key))
  )
    return invalid();
  if (
    value.id !== "home" ||
    !Number.isSafeInteger(value.version) ||
    (value.version as number) < 1 ||
    !["dark", "light"].includes(value.theme as string)
  )
    return invalid();
  for (const [key, limit] of [
    ["siteName", 80],
    ["headline", 80],
    ["body", 300],
  ] as const) {
    const text = value[key];
    if (
      typeof text !== "string" ||
      !text.trim() ||
      text.length > limit ||
      /[\x00-\x1f\x7f]/.test(text)
    )
      return invalid();
  }
  if (value.blocks !== undefined) {
    if (!Array.isArray(value.blocks) || value.blocks.length !== 1) return invalid();
    const logo = value.blocks[0];
    if (
      !logo ||
      typeof logo !== "object" ||
      Array.isArray(logo) ||
      Object.keys(logo).sort().join(",") !== "alt,assetId,id,type" ||
      logo.id !== "header-logo" ||
      logo.type !== "image" ||
      typeof logo.assetId !== "string" ||
      logo.assetId.length > 120 ||
      !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(logo.assetId) ||
      typeof logo.alt !== "string" ||
      logo.alt.length > 300 ||
      /[\x00-\x1f\x7f]/.test(logo.alt)
    )
      return invalid();
    value.blocks = Object.freeze([Object.freeze({ ...logo })]);
  }
  return Object.freeze({ ...value }) as BrandContent;
}

export function resolveBrandLogo(
  content: BrandContent,
  registry: unknown,
): { src: string; alt: string } | null {
  const logo = content.blocks?.[0];
  if (!logo) return null;
  const assets =
    registry && typeof registry === "object" && !Array.isArray(registry)
      ? (registry as { assets?: unknown }).assets
      : null;
  const asset =
    assets && typeof assets === "object" && Object.hasOwn(assets, logo.assetId)
      ? (assets as Record<string, unknown>)[logo.assetId]
      : null;
  if (
    !asset ||
    typeof asset !== "object" ||
    Array.isArray(asset) ||
    Object.keys(asset).join(",") !== "file" ||
    (asset as { file?: unknown }).file !== `${logo.assetId}.webp`
  ) {
    throw new Error("COMMAND_BRAND_ASSET_INVALID");
  }
  return { src: `/assets/${logo.assetId}.webp`, alt: logo.alt };
}
